import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Bell, 
  Send, 
  Users, 
  User, 
  MessageSquare, 
  AlertCircle, 
  Clock,
  CheckCircle,
  Eye,
  EyeOff,
  Loader2,
  Plus,
  Filter,
  Search
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { gymDarkRowHover } from '../gym/gymPageTheme';

const NotificationManager = () => {
  const { getAuthenticatedApi, isGym, isTrainer } = useAuth();
  const { t } = useTranslation();
  const api = getAuthenticatedApi();

  // Stati principali
  const [activeTab, setActiveTab] = useState('send'); // 'send' o 'sent'
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Stati per form invio
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'message',
    priority: 'normal',
    recipient_id: null,
    is_broadcast: false
  });
  const [recipients, setRecipients] = useState([]);
  const [sending, setSending] = useState(false);

  // Stati per filtri
  const [filters, setFilters] = useState({
    type: '',
    priority: '',
    status: ''
  });

  useEffect(() => {
    if (activeTab === 'sent') {
      loadSentNotifications();
    }
    loadRecipients();
  }, [activeTab]);

  const loadRecipients = async () => {
    try {
      // Carica solo i membri (ruolo 'user') della palestra
      const response = await api.get('/users.php?role_name=user');
      setRecipients(response.data);
    } catch (err) {
      console.error('Errore nel caricamento dei destinatari:', err);
      setError(t('gym.notifications.sendForm.errors.sendError'));
    }
  };

  const loadSentNotifications = async () => {
    setLoading(true);
    try {
      const response = await api.get('/notifications.php?action=sent');
      setNotifications(response.data.notifications || []);
    } catch (err) {
      setError(t('gym.notifications.sendForm.errors.sendError'));
      console.error('Errore:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.message.trim()) {
      setError(t('gym.notifications.sendForm.errors.titleMessageRequired'));
      return;
    }

    if (!formData.is_broadcast && !formData.recipient_id) {
      setError(t('gym.notifications.sendForm.errors.recipientRequired'));
      return;
    }

    setSending(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        recipient_id: formData.is_broadcast ? null : formData.recipient_id
      };

      // Invia notifica (in-app + push notification integrate)
      const response = await api.post('/notifications.php', payload);
      
      if (response.data.success) {
        // Reset form
        setFormData({
          title: '',
          message: '',
          type: 'message',
          priority: 'normal',
          recipient_id: null,
          is_broadcast: false
        });
        
        // Mostra messaggio di successo
        alert(t('gym.notifications.sendForm.success'));
        
        // Ricarica le notifiche inviate se siamo nella tab corretta
        if (activeTab === 'sent') {
          loadSentNotifications();
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || t('gym.notifications.sendForm.errors.sendError'));
      console.error('Errore:', err);
    } finally {
      setSending(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'announcement': return <AlertCircle className="h-4 w-4" />;
      case 'reminder': return <Clock className="h-4 w-4" />;
      default: return <MessageSquare className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-950/50';
      case 'normal':
        return 'text-blue-700 bg-blue-50 dark:text-blue-300 dark:bg-blue-950/50';
      case 'low':
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-800';
      default:
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'read': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'delivered': return <Eye className="h-4 w-4 text-blue-600" />;
      default: return <EyeOff className="h-4 w-4 text-gray-400" />;
    }
  };

  // Verifica se l'utente può gestire notifiche
  if (!isGym() && !isTrainer()) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {t('gym.notifications.accessDenied.title')}
        </h3>
        <p className="text-gray-600">
          {t('gym.notifications.accessDenied.message')}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Bell className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t('gym.notifications.title')}
          </h1>
        </div>
        <p className="text-gray-600 dark:text-white/65">
          {t('gym.notifications.description')}
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200 dark:border-white/10">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('send')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'send'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-white/65 dark:hover:border-white/20 dark:hover:text-white'
              }`}
            >
              <Send className="h-4 w-4 inline mr-2" />
              {t('gym.notifications.tabs.send')}
            </button>
            <button
              onClick={() => setActiveTab('sent')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'sent'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-white/65 dark:hover:border-white/20 dark:hover:text-white'
              }`}
            >
              <MessageSquare className="h-4 w-4 inline mr-2" />
              {t('gym.notifications.tabs.sent')}
            </button>
          </nav>
        </div>
      </div>

      {/* Contenuto Tab */}
      {activeTab === 'send' ? (
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-white/10 p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
            {t('gym.notifications.sendForm.title')}
          </h2>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Titolo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                {t('gym.notifications.sendForm.fields.title')} *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-white/30"
                placeholder={t('gym.notifications.sendForm.fields.titlePlaceholder')}
                required
              />
            </div>

            {/* Messaggio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                {t('gym.notifications.sendForm.fields.message')} *
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-white/30"
                placeholder={t('gym.notifications.sendForm.fields.messagePlaceholder')}
                required
              />
            </div>

            {/* Tipo e Priorità */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                  {t('gym.notifications.sendForm.fields.type')}
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-white/30"
                >
                  <option value="message">{t('gym.notifications.sendForm.types.message')}</option>
                  <option value="announcement">{t('gym.notifications.sendForm.types.announcement')}</option>
                  <option value="reminder">{t('gym.notifications.sendForm.types.reminder')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                  {t('gym.notifications.sendForm.fields.priority')}
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-white/30"
                >
                  <option value="low">{t('gym.notifications.sendForm.priorities.low')}</option>
                  <option value="normal">{t('gym.notifications.sendForm.priorities.normal')}</option>
                  <option value="high">{t('gym.notifications.sendForm.priorities.high')}</option>
                </select>
              </div>
            </div>

            {/* Destinatari */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                {t('gym.notifications.sendForm.fields.recipients')}
              </label>
              
              {/* Checkbox Broadcast */}
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_broadcast"
                    checked={formData.is_broadcast}
                    onChange={handleInputChange}
                    className="h-4 w-4 rounded border-gray-300 bg-white text-blue-600 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-white/85">
                    <Users className="h-4 w-4 inline mr-1" />
                    {t('gym.notifications.sendForm.broadcast.label')}
                  </span>
                </label>
              </div>

              {/* Selezione destinatario singolo */}
              {!formData.is_broadcast && (
                <select
                  name="recipient_id"
                  value={formData.recipient_id || ''}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-white/30"
                  required={!formData.is_broadcast}
                >
                  <option value="">{t('gym.notifications.sendForm.selectMember')}</option>
                  {recipients.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.username})
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Pulsanti */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setFormData({
                  title: '',
                  message: '',
                  type: 'message',
                  priority: 'normal',
                  recipient_id: null,
                  is_broadcast: false
                })}
                className="px-4 py-2 text-gray-700 dark:text-white/85 bg-gray-200 dark:bg-gray-800 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-700 transition-colors"
              >
                {t('gym.notifications.sendForm.buttons.cancel')}
              </button>
              <button
                type="submit"
                disabled={sending}
                className="flex items-center rounded-lg bg-blue-600 px-6 py-2 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-indigo-600 dark:hover:bg-indigo-700"
              >
                {sending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                <Send className="h-4 w-4 mr-2" />
                {t('gym.notifications.sendForm.buttons.send')}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-white/10">
          {/* Header con filtri */}
          <div className="p-6 border-b border-gray-200 dark:border-white/10">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('gym.notifications.sentNotifications.title')}
              </h2>
              <button
                onClick={loadSentNotifications}
                className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-white/85 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                {t('gym.notifications.sentNotifications.refresh')}
              </button>
            </div>
          </div>

          {/* Lista notifiche */}
          <div className="divide-y divide-gray-200 dark:divide-white/10">
            {loading ? (
              <div className="p-8 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400 dark:text-white/55 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-white/65">{t('gym.notifications.sentNotifications.loading')}</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <MessageSquare className="h-12 w-12 text-gray-400 dark:text-white/55 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  {t('gym.notifications.sentNotifications.empty.title')}
                </h3>
                <p className="text-gray-600 dark:text-white/65">
                  {t('gym.notifications.sentNotifications.empty.message')}
                </p>
              </div>
            ) : (
              notifications.map(notification => (
                <div key={notification.id} className={`p-6 transition-colors ${gymDarkRowHover}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {getTypeIcon(notification.type)}
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                          {notification.title}
                        </h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(notification.priority)}`}>
                          {notification.priority}
                        </span>
                      </div>
                      
                      <p className="text-gray-600 dark:text-white/65 mb-3">
                        {notification.message}
                      </p>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-white/65">
                        <span>
                          {notification.is_broadcast ? (
                            <>
                              <Users className="h-4 w-4 inline mr-1" />
                              {t('gym.notifications.sendForm.broadcast.allMembers')}
                            </>
                          ) : (
                            <>
                              <User className="h-4 w-4 inline mr-1" />
                              {notification.recipient_name}
                            </>
                          )}
                        </span>
                        <span>
                          <Clock className="h-4 w-4 inline mr-1" />
                          {new Date(notification.created_at).toLocaleString('it-IT')}
                        </span>
                        {notification.is_broadcast && (
                          <span>
                            <Eye className="h-4 w-4 inline mr-1" />
                            {notification.read_count}/{notification.delivered_count} {t('gym.notifications.sentNotifications.stats.read')}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {getStatusIcon(notification.status)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationManager;
